/**
 * ImpersonationBanner — the persistent "you are viewing as X" strip
 * shown at the top of every layout while an admin's session has been
 * swapped into a customer account via `startImpersonation`.
 *
 * Design goals:
 *   • Impossible to miss (amber, sticky, top of viewport).
 *   • One-click return path — clicking either the banner body or the
 *     dedicated button drops us back into the admin session and lands
 *     on the admin dashboard.
 *   • Silent when there's nothing to show — no layout thrash for the
 *     99% of pageviews that are non-impersonated.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { UserCog, ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';

export const ImpersonationBanner = () => {
  const { impersonating, user, stopImpersonation } = useAuth();
  const navigate = useNavigate();
  if (!impersonating || !user) return null;

  const handleExit = async () => {
    const admin = await stopImpersonation();
    // Land back where admins expect to be after a "return".
    navigate(admin?.role === 'admin' ? '/admin' : '/');
  };

  return (
    <div
      className="sticky top-0 z-50 w-full bg-amber-500 text-slate-900 shadow-md"
      data-testid="impersonation-banner"
    >
      <div className="max-w-full px-4 sm:px-6 py-2 flex items-center gap-3 text-sm">
        <UserCog className="h-4 w-4 shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="font-semibold">Viewing as customer:</span>{' '}
          <span className="truncate">{user.name || user.email}</span>
        </div>
        <Button
          size="sm"
          variant="secondary"
          className="bg-slate-900 text-amber-300 hover:bg-slate-800 hover:text-amber-200 h-7 px-2 sm:px-3 text-xs"
          onClick={handleExit}
          data-testid="impersonation-exit-btn"
        >
          <ArrowLeft className="h-3.5 w-3.5 mr-1" />
          Return to admin
        </Button>
      </div>
    </div>
  );
};

export default ImpersonationBanner;
